import { useState } from "react";
import { config, sharedUniversal } from "roo-shared";
import { signUp, signUpWithApple, signUpWithGoogle } from "../services/session.service.js";
import type { SocialAuthResult } from "../services/session.service.js";
import { SocialSignIn } from "../components/SocialSignIn.comp.js";
import type { AppleCredential } from "../services/socialAuth.service.js";

/**
 * Create an account: first name, last name, email. One step — the server's `signUp` creates the
 * user, seeds an empty budget, and returns a session in a single call, with no invite code and no
 * email verification (verification happens later in the mobile onboarding flow).
 *
 * Deliberately NOT a port of mobile's six-page createAccount flow. That flow carries beta-invite
 * gating, money-goal questions and a welcome video, all of which belong to first-run onboarding —
 * which stays on the phone under the desktop-companion scope.
 */

/** Good enough to catch a typo before spending a round trip; the server is the real authority. */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Outside prod, prefill with a throwaway identity — the same helpers mobile's createAccount flow
 * uses (`generateRandomName` / `generateRandomEmail` → `<6-digit>@example.com`), so a developer can
 * make a fresh account in two clicks instead of inventing an email every time.
 *
 * `example.com` is reserved by RFC 2606 precisely so it can never reach a real inbox — which
 * matters here because signup is the one flow that would otherwise email a stranger.
 *
 * Computed once via lazy `useState` rather than in an effect, so it doesn't clobber what the user
 * has typed on a re-render.
 */
function devPrefill() {
  if (config.ENV === "prod") return { firstName: "", lastName: "", email: "" };
  return {
    firstName: sharedUniversal.generateRandomName(),
    lastName: sharedUniversal.generateRandomName(),
    email: sharedUniversal.generateRandomEmail(),
  };
}

export function SignUpPage(p: {
  onBackToLogin: () => void;
  /**
   * Why the user landed here. Set when a social login found no account — without it the redirect
   * reads as the app having lost their tap, which is how a user ends up trying Google twice.
   */
  notice?: string;
}) {
  const [fields, setFields] = useState(devPrefill);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const set = (patch: Partial<typeof fields>) => setFields((prev) => ({ ...prev, ...patch }));

  /**
   * A provider credential, exchanged for an account. Both endpoints are signup-OR-login, so an
   * existing user who arrived here by mistake is signed in rather than told their account already
   * exists.
   */
  async function onSocialSignUp(attempt: () => Promise<SocialAuthResult>) {
    setError(null);
    setIsBusy(true);
    try {
      const result = await attempt();
      if (!result.ok) setError(result.message);
    } catch {
      setError("We couldn't reach Roo. Check your connection and try again.");
    } finally {
      setIsBusy(false);
    }
  }

  const onGoogleIdToken = (identityToken: string) => onSocialSignUp(() => signUpWithGoogle(identityToken));
  // The name is forwarded because Apple releases it exactly once, on this first authorization.
  const onAppleCredential = (credential: AppleCredential) => onSocialSignUp(() => signUpWithApple(credential));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!fields.firstName.trim() || !fields.lastName.trim()) {
      setError("Enter your first and last name.");
      return;
    }
    if (!looksLikeEmail(fields.email)) {
      setError("Enter a valid email address.");
      return;
    }
    setIsBusy(true);
    try {
      // On success this flips session state and App swaps this page out — no navigation here.
      const result = await signUp({
        emailRaw: fields.email.trim(),
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
      });
      if (!result.ok) setError(result.message);
    } catch {
      setError("We couldn't reach Roo. Check your connection and try again.");
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <main className="card" data-testid="signup-page">
      <h1 className="logo">roo</h1>
      {p.notice ? (
        <p className="notice" role="status" data-testid="signup-notice">
          {p.notice}
        </p>
      ) : null}

      <form onSubmit={onSubmit} data-testid="signup-form" noValidate>
        <h2>Create your account</h2>
        <p className="hint">No password — we'll email you a code whenever you sign in.</p>

        <label htmlFor="firstName">First name</label>
        <input
          id="firstName"
          data-testid="signup-first-name"
          type="text"
          autoComplete="given-name"
          autoFocus
          placeholder="First name"
          value={fields.firstName}
          onChange={(e) => set({ firstName: e.target.value })}
          disabled={isBusy}
        />

        <label htmlFor="lastName">Last name</label>
        <input
          id="lastName"
          data-testid="signup-last-name"
          type="text"
          autoComplete="family-name"
          placeholder="Last name"
          value={fields.lastName}
          onChange={(e) => set({ lastName: e.target.value })}
          disabled={isBusy}
        />

        <label htmlFor="signupEmail">Email</label>
        <input
          id="signupEmail"
          data-testid="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={fields.email}
          onChange={(e) => set({ email: e.target.value })}
          disabled={isBusy}
        />

        <button type="submit" data-testid="signup-submit" disabled={isBusy}>
          {isBusy ? "Creating…" : "Create account"}
        </button>
        <button
          type="button"
          className="link"
          data-testid="signup-to-login"
          onClick={p.onBackToLogin}
          disabled={isBusy}
        >
          Already have an account? Log in
        </button>
      </form>

      <SocialSignIn
        testIdPrefix="signup"
        onGoogleIdToken={onGoogleIdToken}
        onAppleCredential={onAppleCredential}
        disabled={isBusy}
      />

      {error ? (
        <p className="error" role="alert" data-testid="signup-error">
          {error}
        </p>
      ) : null}
    </main>
  );
}
